import type { MailboxInfo } from "owa-client-types";
import { setTryRemoveFromMailStoreItemsFunction, setIsMailStoreItemHeldFunction, type RemoveItemSource } from "owa-mail-actions/lib/triage/tryRemoveFromMailStoreItems";
import getAllTableViewsContainingItems from "owa-mail-triage-table-utils/lib/getAllTableViewsContainingItems";
import { getRowKeysFromRowIds, isMailListConversationView } from "owa-mail-list-store";
import store from "owa-mail-store/lib/store/Store";
import { mutatorAction } from "satcheljs";
import { isItemHeldByConversationItemParts, isItemHeldByItemReadingPane, } from "owa-mail-reading-pane-store";

/**
 * True if the item is still referenced by a conversation, the item reading pane, or a list view
 * table row (and so cannot be removed from the mail store right now). Shared by the actual removal
 * below and by {@link setIsMailStoreItemHeldFunction} so callers can check this before doing
 * destructive/expensive work (e.g. decompressing a cached body) in anticipation of a removal that
 * may not actually happen.
 */
function isItemHeld(itemId: string, removeItemSource: RemoveItemSource): boolean {
    const item = store().items.get(itemId);
    if (!item) {
        // Already removed, so nothing to hold onto
        return false;
    }
    
    return (isItemHeldByConversationItemParts(item.ConversationId?.Id, removeItemSource) ||
        isItemHeldByItemReadingPane(itemId, removeItemSource) ||
        isItemHeldByListViewTable(itemId, item.MailboxInfo, item.ParentFolderId?.Id));
}

/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * This is ok as we are just setting a function variable */
setIsMailStoreItemHeldFunction(isItemHeld);

/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * This is ok as we are just setting a function variable */
setTryRemoveFromMailStoreItemsFunction((itemIdToRemove: string, removeItemSource: RemoveItemSource, skipMutator?: boolean) => {
    const itemId = itemIdToRemove;
    
    // If the item has already been removed, NOOP
    // Note this can happen right now because reading pane code calls items.delete(itemId) multiple times for the same delete
    // from deleteConversationItemParts and cleanUpItemClientSide.
    if (!store().items.has(itemId)) {
        return;
    }
    
    if (isItemHeld(itemId, removeItemSource)) {
        // The item reference is currently held, we cannot remove it at this time
        return;
    }
    
    // Delete the item since it's no longer referenced
    (skipMutator ? deleteMailStoreItem : deleteMailStoreItemMutator)(itemId);
});

const deleteMailStoreItemMutator = mutatorAction("deleteMailStoreItem", deleteMailStoreItem);

function deleteMailStoreItem(itemId: string): void {
    store().items.delete(itemId);
}

function isItemHeldByListViewTable(itemId: string, mailboxInfo: MailboxInfo, parentFolderId: string | undefined): boolean {
    if (!parentFolderId) {
        return false;
    }
    
    // Don't check removeItemSource here. Even if it's from a listview table,
    // another listview table may be holding on to this item
    
    const tableViewsForFolder = getAllTableViewsContainingItems(mailboxInfo, parentFolderId, true /* shouldIncludeSearchTable */);
    
    for (const tableView of tableViewsForFolder) {
        if (isMailListConversationView(tableView)) {
            // Only message listviews can hold references to message items
            continue;
        }
        
        const rowKeys = getRowKeysFromRowIds([itemId], tableView);
        for (const rowKey of rowKeys) {
            if (tableView.rowKeys.indexOf(rowKey) >= 0) {
                return true;
            }
        }
    }
    
    return false;
}
